// This is the JSON way to define React Router rules in a Rekit app..
// Learn more from: http://rekit.js.org/docs/routing.html
import {
  NothingSelectedPage,
  Layout,
  TableAndViewer,
  View,
  Theatre,
  TheatreDocOnly,
  TsAndCsModal,
  SettingsWrapper,
  CaseAdminLayout,
  TeamAdmin,
  CaseAdmin,
  CaseSettingsPage,
  CaseRoles,
  Transcript,
  RealTime,
  DialInNumbersPage,
  HearingRoomLogs,
  CaseIntelligencePage,
  CaseIntelligenceVibrantPage,
} from './';
import PresentViewLandingPage from './PresentViewLandingPage';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCases,
  selectCaseIntelligenceVersion,
  selectFetchCasesPending,
  selectIsAuthenticated,
  selectUserSettings,
  selectUseNewRTViewer,
} from 'common/selectors';
import { fetchCases } from 'features/home/redux/actions';
import { kebabCaseToRegular } from 'utils/strings';
import T from 'i18n';
import AiToolsResponseWrapper from './AiToolsResponseWrapper';
import GroupAdmin from './GroupAdmin';
import { normalizeRealTimeTheme } from './RealTime/theme';

const CaseIntelligenceRoute = () => {
  const version = useSelector(selectCaseIntelligenceVersion);
  return version === 'v1' ? <CaseIntelligenceVibrantPage /> : <CaseIntelligencePage />;
};

const PublicHearingRoomTranscriptWrapper = (props) => {
  const dispatch = useDispatch();
  const useNewRTViewer = useSelector(selectUseNewRTViewer);
  const userSettings = useSelector(selectUserSettings);
  const cases = useSelector(selectCases);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const fetchCasesPending = useSelector(selectFetchCasesPending);
  const fetchCaseDetailsPending = useSelector((state) => state.case.fetchCaseDetailsPending);
  const isLoading = fetchCasesPending || fetchCaseDetailsPending;
  const hasRequestedCasesRef = useRef(false);
  const [showBootOverlay, setShowBootOverlay] = React.useState(false);

  useEffect(() => {
    if (!useNewRTViewer) return;
    if (!isAuthenticated) return;
    if (cases.length > 0) return;
    if (hasRequestedCasesRef.current) return;

    hasRequestedCasesRef.current = true;
    dispatch(fetchCases());
  }, [dispatch, isAuthenticated, cases.length]);

  useEffect(() => {
    if (!useNewRTViewer) {
      setShowBootOverlay(false);
      return;
    }

    setShowBootOverlay(true);
    const timer = setTimeout(() => setShowBootOverlay(false), 250);
    return () => clearTimeout(timer);
  }, [useNewRTViewer]);

  const showOverlay = useNewRTViewer && (isLoading || showBootOverlay);
  const realTimeTheme = normalizeRealTimeTheme(userSettings?.realTimeTheme);

  if (!useNewRTViewer) {
    return <Transcript hearingRoomMode="public" {...props} />;
  }

  return (
    <div className={`real-time__route-shell real-time__route-shell--${realTimeTheme}`}>
      {showOverlay && (
        <div className="real-time__loading-overlay">
          <div className="real-time__loading">
            {T.translate('generic.loading', { defaultValue: 'Loading...' })}
          </div>
        </div>
      )}
      <RealTime hearingRoomMode="public" {...props} />
    </div>
  );
};

export default {
  path: '',
  name: '',
  childRoutes: [
    {
      path: 'case/:case',
      component: TsAndCsModal,
      childRoutes: [
        {
          path: 'settings',
          childRoutes: [
            {
              path: 'role-settings',
              name: 'routes.roleSettings',
              component: (props) => <SettingsWrapper settingsType="rolesSettings" {...props} />,
            },
            {
              path: 'team-admin',
              name: 'routes.teamAdmin',
              component: (props) => <SettingsWrapper settingsType="teamAdmin" {...props} />,
            },
            {
              path: 'case-admin',
              name: 'routes.caseAdmin',
              component: CaseAdminLayout,
              childRoutes: [
                {
                  path: '',
                  name: 'routes.caseAdmin',
                  component: CaseAdmin,
                },
                {
                  path: 'case-settings',
                  name: 'routes.caseSettings',
                  component: CaseSettingsPage,
                },
                {
                  path: 'case-roles',
                  name: 'routes.caseRoles',
                  component: CaseRoles,
                },
                {
                  path: 'dial-in-numbers',
                  name: 'routes.dialInNumbers',
                  component: DialInNumbersPage,
                },
                {
                  path: 'hearing-room-logs',
                  name: 'routes.hearingRoomLogs',
                  component: HearingRoomLogs,
                },
                {
                  path: 'all-users',
                  name: 'routes.caseAdmin',
                  component: (props) => <TeamAdmin isAllUsers={true} {...props} />,
                },
                {
                  path: 'teams/:teamId',
                  name: 'routes.caseAdmin',
                  component: TeamAdmin,
                },
                { path: 'groups/:groupId', name: 'routes.caseAdmin', component: GroupAdmin },
              ],
            },
          ],
        },
        {
          path: 'private-team-room/:type?',
          name: 'routes.teamRoom',
          component: (props) => <Theatre hearingRoomMode="private" {...props} />,
        },
        {
          path: 'private-team-room-transcript',
          name: 'routes.privateRoomTranscript',
          component: (props) => <Transcript hearingRoomMode="private" {...props} />,
        },
        {
          path: 'public-hearing-room/document-presentation',
          name: 'routes.evidenceView',
          component: (props) => <TheatreDocOnly hearingRoomMode="public" {...props} />,
        },
        {
          path: 'public-hearing-room/:type?',
          name: 'routes.hearingRoom',
          component: (props) => <Theatre hearingRoomMode="public" {...props} />,
        },
        {
          path: 'public-hearing-room-transcript',
          name: 'routes.hearingRoomTranscript',
          component: PublicHearingRoomTranscriptWrapper,
          childRoutes: [
            {
              path: 'files/:file',
              name: 'routes.hearingRoomTranscript',
              component: AiToolsResponseWrapper,
            },
          ],
        },
        {
          path: 'private-hearing-room-transcript',
          name: 'routes.privateRoomTranscript',
          component: (props) => <Transcript hearingRoomMode="private" {...props} />,
        },
        // full screen viewer
        { path: 'files/:file', name: ({ file }) => file, component: View },
        {
          path: 'present-view',
          childRoutes: [
            {
              path: 'files/:file',
              component: View,
              key: 'presentView', // Stable key to prevent re-mounting
              name: ({ file }) => `Present View – ${file}`,
            },
            {
              path: '',
              component: PresentViewLandingPage,
              key: 'presentViewLanding',
              name: 'routes.presentView',
            },
          ],
        },
        {
          path: '',
          component: Layout,
          childRoutes: [
            { path: '', name: 'routes.nothingSelected', component: NothingSelectedPage },
            {
              path: 'case-intelligence',
              childRoutes: [
                {
                  path: 'files/:file',
                  component: CaseIntelligenceRoute,
                  key: 'case-intelligence-file',
                  name: ({ file }) => `${T.translate('generic.caseIntelligence')} - ${file}`,
                },
                {
                  path: '',
                  component: CaseIntelligenceRoute,
                  key: 'case-intelligence',
                  name: 'routes.caseIntelligence',
                },
              ],
            },
            {
              path: 'all-documents',
              childRoutes: [
                {
                  path: 'files/:file',
                  component: TableAndViewer,
                  key: 'all-docs',
                  name: ({ file }) => `${T.translate('routes.allDocuments')} - ${file}`,
                },
                {
                  path: '',
                  component: TableAndViewer,
                  key: 'all-docs',
                  name: 'routes.allDocuments',
                },
              ],
            },
            {
              path: 'recycle-bin',
              childRoutes: [
                {
                  path: 'files/:file',
                  component: TableAndViewer,
                  key: 'recycle-bin',
                  name: ({ file }) => `${T.translate('routes.recycleBin')} - ${file}`,
                },
                {
                  path: '',
                  component: TableAndViewer,
                  key: 'recycle-bin',
                  name: 'routes.recycleBin',
                },
              ],
            },
            {
              path: 'all-annotations',
              childRoutes: [
                {
                  path: 'files/:file',
                  component: TableAndViewer,
                  key: 'all-annotations',
                  name: ({ file }) => `${T.translate('routes.allAnnotations')} - ${file}`,
                },
                {
                  path: 'files/:file/annotationId/:annotationId',
                  component: TableAndViewer,
                  key: 'all-annotations',
                  name: ({ file }) => `${T.translate('routes.allAnnotations')} - ${file}`,
                },
                {
                  path: '',
                  component: TableAndViewer,
                  key: 'all-annotations',
                  name: 'routes.allAnnotations',
                },
              ],
            },
            {
              path: 'search-mode',
              childRoutes: [
                {
                  path: 'files/:file',
                  component: TableAndViewer,
                  key: 'search',
                  name: ({ file }) => `${T.translate('routes.searchMode')} - ${file}`,
                },
                {
                  path: '',
                  component: TableAndViewer,
                  key: 'search',
                  name: 'routes.searchMode',
                },
              ],
            },
            {
              path: 'ai-tools',
              childRoutes: [
                {
                  path: ':aiSearchId/files/:file',
                  component: TableAndViewer,
                  key: 'aiTools',
                  name: ({ file }) => `${T.translate('routes.aiTools')} - ${file}`,
                },
                {
                  path: ':aiSearchId',
                  component: TableAndViewer,
                  key: 'aiTools',
                  name: 'routes.aiTools',
                },
                {
                  path: 'files/:file',
                  component: TableAndViewer,
                  key: 'aiTools',
                  name: ({ file }) => `${T.translate('routes.aiTools')} - ${file}`,
                },
                {
                  path: '',
                  component: TableAndViewer,
                  key: 'aiTools',
                  name: 'routes.aiTools',
                },
              ],
            },
            {
              path: 'docFinder',
              childRoutes: [
                {
                  path: 'files/:file',
                  component: TableAndViewer,
                  key: 'docFinder',
                  name: ({ file }) => `${T.translate('routes.docFinder')} - ${file}`,
                },
                {
                  path: '',
                  component: TableAndViewer,
                  key: 'docFinder',
                  name: 'routes.docFinder',
                },
              ],
            },
            {
              path: 'page-refs',
              childRoutes: [
                {
                  path: 'files/:file',
                  component: TableAndViewer,
                  key: 'page-refs',
                  name: ({ file }) => `${T.translate('routes.pageRefs')} - ${file}`,
                },
                {
                  path: '',
                  component: TableAndViewer,
                  key: 'page-refs',
                  name: 'routes.pageRefs',
                },
              ],
            },
            {
              path: 'trialbundles',
              childRoutes: [
                {
                  path: ':trialbook/:folder?/files/:file',
                  component: TableAndViewer,
                  key: 'trialbooks-folder',
                  name: ({ folder, trialbook }) => kebabCaseToRegular(folder || trialbook),
                },
                {
                  path: ':trialbook/:folder?/files/:file/compositeKey/:compositeKey',
                  component: TableAndViewer,
                  key: 'trialbooks-folder',
                  name: ({ folder, trialbook }) => kebabCaseToRegular(folder || trialbook),
                },
                {
                  path: ':trialbook/:folder/:folder1?/files/:file',
                  component: TableAndViewer,
                  key: 'trialbooks-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1?/files/:file/compositeKey/:compositeKey',
                  component: TableAndViewer,
                  key: 'trialbooks-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1/:folder2?/files/:file',
                  component: TableAndViewer,
                  key: 'trialbooks-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1/:folder2?/files/:file/compositeKey/:compositeKey',
                  component: TableAndViewer,
                  key: 'trialbooks-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1/:folder2/:folder3?/files/:file',
                  component: TableAndViewer,
                  key: 'trialbooks-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1/:folder2/:folder3?/files/:file/compositeKey/:compositeKey',
                  component: TableAndViewer,
                  key: 'trialbooks-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1/:folder2/:folder3/:folder4?/files/:file',
                  component: TableAndViewer,
                  key: 'trialbooks-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1/:folder2/:folder3/:folder4?/files/:file/compositeKey/:compositeKey',
                  component: TableAndViewer,
                  key: 'trialbooks-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook',
                  component: TableAndViewer,
                  key: 'trialbooks-folder',
                  name: ({ folder, trialbook }) => kebabCaseToRegular(folder || trialbook),
                },
                {
                  path: ':trialbook/:folder',
                  component: TableAndViewer,
                  key: 'trialbooks-folder',
                  name: ({ folder, trialbook }) => kebabCaseToRegular(folder || trialbook),
                },
                {
                  path: ':trialbook/:folder/:folder1',
                  component: TableAndViewer,
                  key: 'trialbooks-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1/:folder2',
                  component: TableAndViewer,
                  key: 'trialbooks-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1/:folder2/:folder3',
                  component: TableAndViewer,
                  key: 'trialbooks-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':trialbook/:folder/:folder1/:folder2/:folder3/:folder4',
                  component: TableAndViewer,
                  key: 'trialbooks-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
              ],
            },
            {
              path: 'bundles',
              name: 'routes.bundles',
              childRoutes: [
                {
                  path: ':bundle/:folder?/files/:file',
                  component: TableAndViewer,
                  key: 'bundle-folder',
                  name: ({ folder, bundle }) => kebabCaseToRegular(folder || bundle),
                },
                {
                  path: ':bundle/:folder/:folder1?/files/:file',
                  component: TableAndViewer,
                  key: 'bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':bundle/:folder/:folder1/:folder2?/files/:file',
                  component: TableAndViewer,
                  key: 'bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':bundle/:folder/:folder1/:folder2/:folder3?/files/:file',
                  component: TableAndViewer,
                  key: 'bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':bundle/:folder/:folder1/:folder2/:folder3/:folder4?/files/:file',
                  component: TableAndViewer,
                  key: 'bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':bundle',
                  component: TableAndViewer,
                  key: 'bundle-folder',
                  name: ({ folder, bundle }) => kebabCaseToRegular(folder || bundle),
                },
                {
                  path: ':bundle/:folder',
                  component: TableAndViewer,
                  key: 'bundle-folder',
                  name: ({ folder, bundle }) => kebabCaseToRegular(folder || bundle),
                },
                {
                  path: ':bundle/:folder/:folder1',
                  component: TableAndViewer,
                  key: 'bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':bundle/:folder/:folder1/:folder2',
                  component: TableAndViewer,
                  key: 'bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':bundle/:folder/:folder1/:folder2/:folder3',
                  component: TableAndViewer,
                  key: 'bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':bundle/:folder/:folder1/:folder2/:folder3/:folder4',
                  component: TableAndViewer,
                  key: 'bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
              ],
            },
            {
              path: 'teamBundles',
              name: 'routes.teamBundles',
              childRoutes: [
                {
                  path: ':teamBundle/:folder?/files/:file',
                  component: TableAndViewer,
                  key: 'team-bundle-folder',
                  name: ({ folder, teamBundle }) => kebabCaseToRegular(folder || teamBundle),
                },
                {
                  path: ':teamBundle/:folder/:folder1?/files/:file',
                  component: TableAndViewer,
                  key: 'team-bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':teamBundle/:folder/:folder1/:folder2?/files/:file',
                  component: TableAndViewer,
                  key: 'team-bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':teamBundle/:folder/:folder1/:folder2/:folder3?/files/:file',
                  component: TableAndViewer,
                  key: 'team-bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':teamBundle/:folder/:folder1/:folder2/:folder3/:folder4?/files/:file',
                  component: TableAndViewer,
                  key: 'team-bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':teamBundle',
                  component: TableAndViewer,
                  key: 'team-bundle-folder',
                  name: ({ folder, teamBundle }) => kebabCaseToRegular(folder || teamBundle),
                },
                {
                  path: ':teamBundle/:folder',
                  component: TableAndViewer,
                  key: 'team-bundle-folder',
                  name: ({ folder, teamBundle }) => kebabCaseToRegular(folder || teamBundle),
                },
                {
                  path: ':teamBundle/:folder/:folder1',
                  component: TableAndViewer,
                  key: 'team-bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':teamBundle/:folder/:folder1/:folder2',
                  component: TableAndViewer,
                  key: 'team-bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':teamBundle/:folder/:folder1/:folder2/:folder3',
                  component: TableAndViewer,
                  key: 'team-bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':teamBundle/:folder/:folder1/:folder2/:folder3/:folder4',
                  component: TableAndViewer,
                  key: 'team-bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
              ],
            },
            {
              path: 'privateBundles',
              name: 'routes.privateBundles',
              childRoutes: [
                {
                  path: ':privateBundle/:folder?/files/:file',
                  component: TableAndViewer,
                  key: 'private-bundle-folder',
                  name: ({ folder, privateBundle }) => kebabCaseToRegular(folder || privateBundle),
                },
                {
                  path: ':privateBundle/:folder/:folder1?/files/:file',
                  component: TableAndViewer,
                  key: 'private-bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':privateBundle/:folder/:folder1/:folder2?/files/:file',
                  component: TableAndViewer,
                  key: 'private-bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':privateBundle/:folder/:folder1/:folder2/:folder3?/files/:file',
                  component: TableAndViewer,
                  key: 'private-bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':privateBundle/:folder/:folder1/:folder2/:folder3/:folder4?/files/:file',
                  component: TableAndViewer,
                  key: 'private-bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':privateBundle',
                  component: TableAndViewer,
                  key: 'private-bundle-folder',
                  name: ({ folder, privateBundle }) => kebabCaseToRegular(folder || privateBundle),
                },
                {
                  path: ':privateBundle/:folder',
                  component: TableAndViewer,
                  key: 'private-bundle-folder',
                  name: ({ folder, privateBundle }) => kebabCaseToRegular(folder || privateBundle),
                },
                {
                  path: ':privateBundle/:folder/:folder1',
                  component: TableAndViewer,
                  key: 'private-bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':privateBundle/:folder/:folder1/:folder2',
                  component: TableAndViewer,
                  key: 'private-bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':privateBundle/:folder/:folder1/:folder2/:folder3',
                  component: TableAndViewer,
                  key: 'private-bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':privateBundle/:folder/:folder1/:folder2/:folder3/:folder4',
                  component: TableAndViewer,
                  key: 'private-bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
              ],
            },
            {
              path: 'court-bundles',
              name: 'routes.courtBundles',
              childRoutes: [
                {
                  path: ':courtBundle/:folder?/files/:file',
                  component: TableAndViewer,
                  key: 'court-bundle-folder',
                  name: ({ folder, courtBundle }) => kebabCaseToRegular(folder || courtBundle),
                },
                {
                  path: ':courtBundle/:folder/:folder1?/files/:file',
                  component: TableAndViewer,
                  key: 'court-bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':courtBundle/:folder/:folder1/:folder2?/files/:file',
                  component: TableAndViewer,
                  key: 'court-bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':courtBundle/:folder/:folder1/:folder2/:folder3?/files/:file',
                  component: TableAndViewer,
                  key: 'court-bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':courtBundle/:folder/:folder1/:folder2/:folder3/:folder4?/files/:file',
                  component: TableAndViewer,
                  key: 'court-bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':courtBundle',
                  component: TableAndViewer,
                  key: 'court-bundle-folder',
                  name: ({ folder, courtBundle }) => kebabCaseToRegular(folder || courtBundle),
                },
                {
                  path: ':courtBundle/:folder',
                  component: TableAndViewer,
                  key: 'court-bundle-folder',
                  name: ({ folder, courtBundle }) => kebabCaseToRegular(folder || courtBundle),
                },
                {
                  path: ':courtBundle/:folder/:folder1',
                  component: TableAndViewer,
                  key: 'court-bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':courtBundle/:folder/:folder1/:folder2',
                  component: TableAndViewer,
                  key: 'court-bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':courtBundle/:folder/:folder1/:folder2/:folder3',
                  component: TableAndViewer,
                  key: 'court-bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':courtBundle/:folder/:folder1/:folder2/:folder3/:folder4',
                  component: TableAndViewer,
                  key: 'court-bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
              ],
            },
            {
              path: 'group-bundles',
              name: 'routes.groupBundles',
              childRoutes: [
                {
                  path: ':groupBundle/:folder?/files/:file',
                  component: TableAndViewer,
                  key: 'group-bundle-folder',
                  name: ({ folder, groupBundle }) => kebabCaseToRegular(folder || groupBundle),
                },
                {
                  path: ':groupBundle/:folder/:folder1?/files/:file',
                  component: TableAndViewer,
                  key: 'group-bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':groupBundle/:folder/:folder1/:folder2?/files/:file',
                  component: TableAndViewer,
                  key: 'group-bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':groupBundle/:folder/:folder1/:folder2/:folder3?/files/:file',
                  component: TableAndViewer,
                  key: 'group-bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':groupBundle/:folder/:folder1/:folder2/:folder3/:folder4?/files/:file',
                  component: TableAndViewer,
                  key: 'group-bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':groupBundle',
                  component: TableAndViewer,
                  key: 'group-bundle-folder',
                  name: ({ folder, groupBundle }) => kebabCaseToRegular(folder || groupBundle),
                },
                {
                  path: ':groupBundle/:folder',
                  component: TableAndViewer,
                  key: 'group-bundle-folder',
                  name: ({ folder, groupBundle }) => kebabCaseToRegular(folder || groupBundle),
                },
                {
                  path: ':groupBundle/:folder/:folder1',
                  component: TableAndViewer,
                  key: 'group-bundle-folder1',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':groupBundle/:folder/:folder1/:folder2',
                  component: TableAndViewer,
                  key: 'group-bundle-folder2',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':groupBundle/:folder/:folder1/:folder2/:folder3',
                  component: TableAndViewer,
                  key: 'group-bundle-folder3',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
                {
                  path: ':groupBundle/:folder/:folder1/:folder2/:folder3/:folder4',
                  component: TableAndViewer,
                  key: 'group-bundle-folder4',
                  name: ({ folder }) => kebabCaseToRegular(folder),
                },
              ],
            },
            {
              path: 'quick-links',
              name: 'routes.quickLinks',
              childRoutes: [
                {
                  path: ':quickLink/:folder?/files/:file',
                  component: TableAndViewer,
                  key: 'quick-links-folder',
                  name: ({ folder, quickLink }) => kebabCaseToRegular(folder || quickLink),
                },
                {
                  path: ':quickLink/:folder1/:folder?/files/:file',
                  component: TableAndViewer,
                  key: 'quick-links-folder',
                  name: ({ folder, quickLink }) => kebabCaseToRegular(folder || quickLink),
                },
                {
                  path: ':quickLink/:folder?/files/:file/annotationId/:annotationId',
                  component: TableAndViewer,
                  key: 'quick-links-folder',
                  name: ({ folder, quickLink }) => kebabCaseToRegular(folder || quickLink),
                },
                {
                  path: ':quickLink/:folder1/:folder?/files/:file/annotationId/:annotationId',
                  component: TableAndViewer,
                  key: 'quick-links-folder',
                  name: ({ folder, quickLink }) => kebabCaseToRegular(folder || quickLink),
                },
                {
                  path: ':quickLink/:folder?',
                  component: TableAndViewer,
                  key: 'quick-links-folder',
                  name: ({ folder, quickLink }) => kebabCaseToRegular(folder || quickLink),
                },
                {
                  path: ':quickLink/:folder1/:folder?',
                  component: TableAndViewer,
                  key: 'quick-links-folder',
                  name: ({ folder, quickLink }) => kebabCaseToRegular(folder || quickLink),
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
